import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DragDropContext, Droppable } from '@hello-pangea/dnd'
import cx from 'helpers/cx'

import { AddIcon } from '@pleio/icons/add'

import Document from 'js/components/Document'
import ErrorBoundary from 'js/components/ErrorBoundary'
import Errors from 'js/components/Errors'
import Flexer from 'js/components/Flexer/Flexer'
import CopyItem from 'js/components/Item/ItemActions/components/CopyItem'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import NestedNav from 'js/components/NestedNav/NestedNav'
import useUnsavedChangesBlocker from 'js/lib/hooks/useUnsavedChangesBlocker'
import { useTipTapEditorStore } from 'js/lib/stores'
import { PageNavWrapper } from 'js/page/Page/Page'
import PageEditorContext from 'js/page/PageEditor/PageEditorContext'
import ThemeProvider from 'js/theme/ThemeProvider'

import PageEditorHeader from './components/PageEditorHeader'
import WidgetSettings from './components/WidgetSettings/WidgetSettings'
import styles from './PageEditor.module.css'
import PageEditorRow from './PageEditorRow/PageEditorRow'
import usePageEditorDragAndDrop from './usePageEditorDragAndDrop'
import usePageEditorRevisions from './usePageEditorRevisions'
import usePageEditorRows from './usePageEditorRows'
import usePageEditorSubmit from './usePageEditorSubmit'
import usePageEditorWidgetEditing from './usePageEditorWidgetEditing'

const PageEditor = ({ site, viewer, entity, isFooter = false }) => {
    const { t } = useTranslation()

    const [errors, setErrors] = useState()
    const [showCopyPageModal, setShowCopyPageModal] = useState(false)
    const [accessControl, setAccessControl] = useState(
        entity?.accessControl || [],
    )

    const { guid, title, statusPublished, group, rootParent, showNavigation } =
        entity

    const isPublished = statusPublished === 'published'
    const isArchived = statusPublished === 'archived'

    const pageRevisions = usePageEditorRevisions(guid)
    const {
        activeRevision,
        revisionTitle,
        versionParam,
        isResolvingRevision,
        revisionNotFound,
    } = pageRevisions

    // Fall back to the live page when the URL revision cannot be found.
    const effectiveVersionParam = revisionNotFound ? null : versionParam

    const {
        rows,
        setRows,
        isDirty,
        setIsDirty,
        handleAddRow,
        handleDiscardChanges,
    } = usePageEditorRows({
        entityRows: entity.rows,
        activeRevision,
        versionParam: effectiveVersionParam,
    })

    useUnsavedChangesBlocker(isDirty)

    const {
        widgetEditing,
        setWidgetEditing,
        closeWidgetEditing,
        saveWidget,
        isEditingWidget,
    } = usePageEditorWidgetEditing({
        setRows,
    })

    const { isSubmitting, submitForm, restoreRevision } = usePageEditorSubmit({
        guid,
        rows,
        entity,
        accessControl,
        isPublished,
        setErrors,
        setIsDirty,
        versionParam,
    })

    const handleRestoreRevision = useCallback(() => {
        setWidgetEditing(null)
        restoreRevision()
    }, [restoreRevision, setWidgetEditing])

    const { isDragging, handleDragStart, handleDragUpdate, handleDragEnd } =
        usePageEditorDragAndDrop({
            rows,
            setRows,
        })

    const showPageNav = rootParent ? rootParent.showNavigation : showNavigation
    const isFullScreen = useTipTapEditorStore((state) => state.isAnyFullScreen)
    const headerIsHidden = isFullScreen && isEditingWidget

    const pageEditorContextValue = useMemo(
        () => ({
            isDragging,
            rows,
            setRows,
            widgetEditing,
            setWidgetEditing,
            group,
            site,
            pageRevisions: {
                ...pageRevisions,
                restoreRevision: handleRestoreRevision,
            },
        }),
        [
            isDragging,
            rows,
            setRows,
            widgetEditing,
            setWidgetEditing,
            group,
            site,
            pageRevisions,
            handleRestoreRevision,
        ],
    )

    return (
        <ErrorBoundary>
            <PageEditorContext.Provider value={pageEditorContextValue}>
                <Document title={title} />

                {/* Use Pleio theme for some parts to distinguish editor from normal page elements */}
                <ThemeProvider theme="pleio">
                    <PageEditorHeader
                        entity={entity}
                        site={site}
                        viewer={viewer}
                        revisionTitle={revisionTitle}
                        isEditingWidget={isEditingWidget}
                        isHidden={headerIsHidden}
                        isSubmitting={isSubmitting}
                        isArchived={isArchived}
                        isDirty={isDirty}
                        isPublished={isPublished}
                        isFooter={isFooter}
                        group={group}
                        accessControl={accessControl}
                        setAccessControl={setAccessControl}
                        setWidgetEditing={setWidgetEditing}
                        onSaveDraft={() => submitForm({ isDraft: true })}
                        onPublish={() => submitForm({ isDraft: false })}
                        onDiscardChanges={handleDiscardChanges}
                    />

                    <WidgetSettings
                        site={site}
                        widgetEditing={widgetEditing}
                        group={group}
                        onClose={closeWidgetEditing}
                        onSave={saveWidget}
                    />
                </ThemeProvider>

                <PageNavWrapper>
                    {showPageNav && (
                        <NestedNav
                            viewer={viewer}
                            editMode={true}
                            entityType="page"
                            guid={guid}
                        />
                    )}
                    <main
                        className={cx(
                            styles.wrapper,
                            isFooter && styles.isFooter,
                            rows.length > 0 && styles.hasRows,
                            isDragging === 'WIDGET' && styles.isDraggingWidget,
                            isEditingWidget && styles.isEditingWidget,
                            isSubmitting && styles.isSubmitting,
                            showPageNav && styles.showPageNav,
                        )}
                    >
                        {errors && (
                            <Flexer className={styles.errorWrapper}>
                                <Errors errors={errors} />
                            </Flexer>
                        )}

                        {isResolvingRevision ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                <DragDropContext
                                    onDragStart={handleDragStart}
                                    onDragUpdate={handleDragUpdate}
                                    onDragEnd={handleDragEnd}
                                >
                                    <Droppable
                                        droppableId="droppable-rows"
                                        type="ROW"
                                        direction="vertical"
                                    >
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                data-guid={guid}
                                            >
                                                {rows.map((row, index) => (
                                                    <PageEditorRow
                                                        key={index}
                                                        rowIndex={index}
                                                        rows={rows}
                                                        entity={row}
                                                        growIsEnabled={
                                                            !isFooter
                                                        }
                                                    />
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>

                                <button
                                    type="button"
                                    onClick={handleAddRow}
                                    className="PageAddRow"
                                    disabled={isDragging || widgetEditing}
                                    data-testid="button-add-row"
                                >
                                    <div className="PageAddRowButton">
                                        <AddIcon />
                                        <span style={{ marginTop: '8px' }}>
                                            {t('page.add-row')}
                                        </span>
                                    </div>
                                </button>
                            </>
                        )}

                        <CopyItem
                            isVisible={showCopyPageModal}
                            entity={entity}
                            onClose={() => setShowCopyPageModal(false)}
                        />
                    </main>
                </PageNavWrapper>
            </PageEditorContext.Provider>
        </ErrorBoundary>
    )
}

export default PageEditor
